import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
  HomeIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  ReceiptPercentIcon,
  ArchiveBoxIcon,
  CreditCardIcon,
  ChartBarIcon,
  ArrowsRightLeftIcon,
  UserGroupIcon,
  CalculatorIcon,
  ScaleIcon,
  ChatBubbleLeftRightIcon,
  BanknotesIcon,
  DocumentMagnifyingGlassIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  ClockIcon,
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  Cog6ToothIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';

const Navigation = () => {
  const { companyName, logoUrl } = useTenant();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  const isComprasOnly = user?.role === 'compras';

  // Cerrar menús al hacer clic fuera o al presionar Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null);
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') { setOpenMenu(null); setMobileOpen(false); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  // Cerrar el menú al navegar
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // ── Estructura del menú: enlaces directos y grupos desplegables ────────────
  const menu = isComprasOnly
    ? [
        { type: 'link', name: 'Inventario', href: '/inventory', icon: ArchiveBoxIcon },
        { type: 'link', name: 'Asistencia', href: '/attendance', icon: ClockIcon },
      ]
    : [
        { type: 'link', name: 'Dashboard', href: '/', icon: HomeIcon },
        { type: 'link', name: 'Ventas', href: '/sales', icon: CurrencyDollarIcon },
        { type: 'link', name: 'Gastos', href: '/expenses', icon: ReceiptPercentIcon },
        {
          type: 'group', name: 'Inventario', icon: ArchiveBoxIcon,
          items: [
            { name: 'Compras / Facturas', href: '/inventory', icon: ArchiveBoxIcon },
            { name: 'Inventario Inicial', href: '/initial-inventory', icon: ScaleIcon },
            { name: 'Tiendas', href: '/stores', icon: BuildingStorefrontIcon },
          ],
        },
        {
          type: 'group', name: 'Pagos', icon: CreditCardIcon,
          items: [
            { name: 'Cuentas por Pagar', href: '/accounts-payable', icon: CreditCardIcon },
            { name: 'Plan de Pagos', href: '/payment-plans', icon: CalculatorIcon },
            { name: 'Cashea', href: '/cashea', icon: BanknotesIcon },
            { name: 'Cuentas por Cobrar', href: '/cuentas-por-cobrar', icon: UserGroupIcon },
            { name: 'Traspasos', href: '/traspasos', icon: ArrowsRightLeftIcon },
          ],
        },
        {
          type: 'group', name: 'Caja y Bancos', icon: BuildingLibraryIcon,
          items: [
            { name: 'Control de Efectivo', href: '/cash-control', icon: CurrencyDollarIcon },
            { name: 'Disponibilidad Bancaria', href: '/bank-availability', icon: BanknotesIcon },
            { name: 'Conciliación Bancaria', href: '/bank-reconciliation', icon: DocumentMagnifyingGlassIcon },
            { name: 'Conciliación Manual', href: '/conciliacion-manual', icon: DocumentMagnifyingGlassIcon },
            { name: 'Lotes POS vs Banco', href: '/pos-comparison', icon: DocumentMagnifyingGlassIcon },
            { name: 'Dinero en Tránsito', href: '/dinero-en-transito', icon: ArrowsRightLeftIcon },
            { name: 'Tasa de Cambio', href: '/exchange-rate', icon: ArrowsRightLeftIcon },
          ],
        },
        {
          type: 'group', name: 'Reportes', icon: ChartBarIcon,
          items: [
            { name: 'Reportes', href: '/reports', icon: ChartBarIcon },
            { name: 'Resumen Diario', href: '/daily-report', icon: DocumentTextIcon },
            { name: 'Reporte Mensual', href: '/monthly-report', icon: DocumentArrowDownIcon },
            { name: 'Punto de Equilibrio', href: '/break-even', icon: CalculatorIcon },
          ],
        },
        {
          type: 'group', name: 'Personal', icon: UserGroupIcon,
          items: [
            { name: 'Asistencia', href: '/attendance', icon: ClockIcon },
            ...(isAdmin ? [{ name: 'Nómina', href: '/payroll', icon: BanknotesIcon }] : []),
          ],
        },
        ...(isAdmin
          ? [{
              type: 'group', name: 'Administración', icon: Cog6ToothIcon,
              items: [
                { name: 'Usuarios', href: '/users', icon: UserGroupIcon },
                { name: 'Configuración de Bancos', href: '/bank-settings', icon: BuildingLibraryIcon },
                { name: 'Telegram', href: '/whatsapp-setup', icon: ChatBubbleLeftRightIcon },
              ],
            }]
          : []),
      ];

  const isLinkActive = (href) => location.pathname === href;
  const isGroupActive = (group) => group.items.some((i) => isLinkActive(i.href));

  return (
    <nav ref={navRef} className="bg-white shadow-sm border-b border-gray-200 relative z-40">
      <div className="container mx-auto px-4">
        {/* Encabezado: logo, usuario y salir */}
        <div className="flex justify-between items-center h-16 gap-3">
          <div className="flex items-center space-x-3 min-w-0">
            <img
              src={logoUrl || "/farmavida_logo.png"}
              alt={companyName}
              className="h-10 w-10 rounded flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-blue-900 truncate">{companyName}</h1>
              <p className="text-xs text-gray-600">Gestión Financiera</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="text-sm hidden sm:block text-right">
              <span className="text-gray-600">Bienvenido, </span>
              <span className="font-medium text-gray-900">{user?.full_name || user?.username}</span>
              {isAdmin && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Admin
                </span>
              )}
            </div>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap"
            >
              Cerrar Sesión
            </button>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              aria-label="Abrir menú"
            >
              {mobileOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Menú de escritorio: una sola fila con desplegables */}
        <div className="hidden lg:block border-t border-gray-100">
          <div className="flex items-center justify-center flex-wrap gap-x-1 gap-y-1 py-2">
            {menu.map((entry) => {
              const Icon = entry.icon;

              if (entry.type === 'link') {
                const active = isLinkActive(entry.href);
                return (
                  <Link
                    key={entry.name}
                    to={entry.href}
                    className={`${
                      active
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    } px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors duration-200 whitespace-nowrap`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{entry.name}</span>
                  </Link>
                );
              }

              const active = isGroupActive(entry);
              const open = openMenu === entry.name;
              return (
                <div key={entry.name} className="relative">
                  <button
                    onClick={() => setOpenMenu(open ? null : entry.name)}
                    className={`${
                      active
                        ? 'bg-blue-100 text-blue-700'
                        : open
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    } px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors duration-200 whitespace-nowrap`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{entry.name}</span>
                    <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>

                  {open && (
                    <div className="absolute left-0 mt-1 w-60 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {entry.items.map((item) => {
                        const ItemIcon = item.icon;
                        const itemActive = isLinkActive(item.href);
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            className={`${
                              itemActive
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            } flex items-center gap-2.5 px-4 py-2 text-sm`}
                          >
                            <ItemIcon className="h-4 w-4 text-gray-400" />
                            <span>{item.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Menú móvil / pantallas pequeñas */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-gray-100 pb-3">
            <div className="sm:hidden px-3 py-2 text-sm text-gray-600">
              Bienvenido, <span className="font-medium text-gray-900">{user?.full_name || user?.username}</span>
            </div>
            {menu.map((entry) => {
              const Icon = entry.icon;

              if (entry.type === 'link') {
                const active = isLinkActive(entry.href);
                return (
                  <Link
                    key={entry.name}
                    to={entry.href}
                    className={`${
                      active ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                    } flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-md`}
                  >
                    <Icon className="h-5 w-5 text-gray-400" />
                    {entry.name}
                  </Link>
                );
              }

              return (
                <div key={entry.name} className="mt-3">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {entry.name}
                  </h3>
                  <div className="mt-1 space-y-0.5">
                    {entry.items.map((item) => {
                      const ItemIcon = item.icon;
                      const itemActive = isLinkActive(item.href);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={`${
                            itemActive ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                          } flex items-center gap-2.5 pl-6 pr-3 py-2 text-sm rounded-md`}
                        >
                          <ItemIcon className="h-4 w-4 text-gray-400" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
